import { useEffect, useRef, useState } from 'react';

/**
 * Controls skeleton/loading-state visibility with two thresholds:
 *
 * - `delayMs`  (default 300ms): only show the skeleton if loading takes longer
 *              than this. Fast responses skip the skeleton entirely.
 * - `minMs`    (default 1000ms): once the skeleton is shown, keep it visible
 *              for at least this long to avoid a jarring snap.
 */
const useMinimumLoading = (isLoading: boolean, delayMs = 300, minMs = 1000): boolean => {
	const [displayed, setDisplayed] = useState(false);
	const delayTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const minTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const displayedAtRef = useRef<number | null>(null);

	useEffect(() => {
		if (isLoading) {
			if (displayedAtRef.current !== null) return;

			if (minTimerRef.current) {
				clearTimeout(minTimerRef.current);
				minTimerRef.current = null;
			}

			delayTimerRef.current = setTimeout(() => {
				delayTimerRef.current = null;
				setDisplayed(true);
				displayedAtRef.current = Date.now();
			}, delayMs);

			return;
		}

		if (delayTimerRef.current) {
			// Response arrived before delay fired — skip skeleton entirely
			clearTimeout(delayTimerRef.current);
			delayTimerRef.current = null;
			return;
		}

		if (displayedAtRef.current !== null) {
			// Skeleton is showing — keep it visible until minMs has elapsed
			const elapsed = Date.now() - displayedAtRef.current;
			const remaining = minMs - elapsed;

			if (remaining <= 0) {
				setDisplayed(false);
				displayedAtRef.current = null;
			} else {
				minTimerRef.current = setTimeout(() => {
					minTimerRef.current = null;
					setDisplayed(false);
					displayedAtRef.current = null;
				}, remaining);
			}
		}
	}, [isLoading, delayMs, minMs]);

	useEffect(() => {
		return () => {
			if (delayTimerRef.current) clearTimeout(delayTimerRef.current);
			if (minTimerRef.current) clearTimeout(minTimerRef.current);
		};
	}, []);

	return displayed;
};

export default useMinimumLoading;
